import { AxiosInstance } from 'axios';
import { ExactConnection } from './exactConnection';
import { QueueSnapshot } from './queueMonitor';

/**
 * What is wrong with one environment, and what can be done about it.
 *
 * The server does the reasoning here rather than the browser. Not to keep the client thin,
 * but because the conclusions rest on things only that side can see: which function reads
 * which queue, what the host.json of that app allows, and which blob in storage proves an
 * app was alive. A verdict without its evidence is just an opinion, and evidence that never
 * left the server cannot be shown next to it.
 */

/** How much attention something needs. Mirrors HealthSeverity on the server. */
export type HealthSeverity = 'Healthy' | 'Info' | 'Warning' | 'Critical';

/** What the storage evidence says about a function app. Mirrors WorkerVerdict. */
export type WorkerVerdict =
    | 'Unknown'
    | 'Running'
    | 'Alive'
    | 'Silent'
    | 'NoEvidence';

/** The actions a finding can offer. Mirrors FindingActionKind. */
export type FindingActionKind =
    | 'None'
    | 'RefreshExactToken'
    | 'ReconnectExact'
    | 'OpenErrorLog'
    | 'RequeuePoison'
    | 'OfferAgainNow'
    | 'OpenWorkerLog';

export interface FindingAction {
    kind: FindingActionKind;
    label: string;
    description: string;
    queue?: string | null;
    /** What to watch for afterwards: how long it takes and how you tell it worked. */
    expectation?: string | null;
    /** Whether pressing it changes anything, as opposed to only showing something. */
    mutates: boolean;
    /** Why this would be premature right now, or null when it is safe to offer. */
    caveat?: string | null;
}

export interface Finding {
    /** Stable identifier of the rule behind this finding, for keys and for tests. */
    code: string;
    severity: HealthSeverity;
    title: string;
    explanation: string;
    /** The measurement the conclusion rests on. */
    evidence: string;
    queue?: string | null;
    worker?: string | null;
    count?: number | null;
    /** The section of docs/runbook.md covering this. */
    runbookSection?: string | null;
    actions: FindingAction[];
    /** Whether acting on anything else is pointless until this is fixed. */
    blocksOtherWork: boolean;
}

/** What sets a function off. Mirrors WorkerTrigger on the server. */
export type WorkerTrigger = 'Queue' | 'Http' | 'Timer';

/** One function inside a function app. */
export interface WorkerFunction {
    /** The name the runtime knows it by, and the same string as operation_Name. */
    name: string;
    trigger: WorkerTrigger;
    /** The queue it reads, for a queue trigger. */
    queue?: string | null;
    /**
     * The queue it writes to, for a webhook endpoint.
     *
     * Kept apart from `queue` because the pipeline draws a reader after its queue and a
     * writer before it; confusing the two reverses the arrow.
     */
    writesQueue?: string | null;
    /** How often a timer fires; null when its cron lives in an app setting. */
    intervalSeconds?: number | null;
    /** Application Insights, filtered to this function. Null when not configured. */
    logsUrl?: string | null;
    /** The query behind logsUrl, so a dead link still leaves something to paste. */
    query?: string | null;
}

/**
 * Where to click to get from a badge to the app itself in Azure.
 *
 * Every field is optional and the badge leaves out what it does not get. The three links
 * need three different amounts of configuration - a prefix, a subscription plus resource
 * group, and the name of an Application Insights resource - so they fail separately, and
 * `configurationHint` says which piece is missing rather than leaving a gap that reads as a
 * bug.
 */
export interface WorkerLinks {
    appName?: string | null;
    /** Whether the app name rests on the naming convention rather than on configuration. */
    appNameIsDerived: boolean;
    site?: string | null;
    scm?: string | null;
    portal?: string | null;
    portalLogStream?: string | null;
    logsAll?: string | null;
    queryAll?: string | null;
    logsHost?: string | null;
    queryHost?: string | null;
    logsExceptions?: string | null;
    queryExceptions?: string | null;
    configurationHint?: string | null;
}

export interface WorkerStatus {
    key: string;
    displayName: string;
    /** The project this app is built from, so a finding leads back to code. */
    project: string;
    readsQueues: string[];
    /** The suffix Azure knows the app by, for example "exsnd". */
    suffix?: string | null;
    /** The functions inside this app, queue triggers first. */
    functions?: WorkerFunction[] | null;
    links?: WorkerLinks | null;
    verdict: WorkerVerdict;
    /** The last moment this app demonstrably did something. */
    lastSeenAt?: string | null;
    /** The timer function that produced lastSeenAt. */
    lastSeenVia?: string | null;
    nextExpectedAt?: string | null;
    quietForSeconds?: number | null;
    /** The reasoning behind the verdict, written out. */
    evidence: string;
}

/**
 * How far the payment sync from Exact has got. Mirrors PaymentSyncDto.
 *
 * Read from the connector's own reference table, so it is there even where the heartbeat of
 * the function apps is out of sight. A fresh mark proves the timer ran; an old one only says
 * that Exact had no new settled receivables since.
 */
export interface PaymentSync {
    /** Whether the feature that switches this route on is on; null when it does not exist. */
    enabled?: boolean | null;
    /** The Modified date of the last receivable handled. Older ones are not looked at again. */
    processedUpTo?: string | null;
    /** When the timer last wrote that mark. */
    lastAdvancedAt?: string | null;
    error?: string | null;
}

export interface WorkerFleet {
    customer: string;
    environment: string;
    sampledAt: string;
    workers: WorkerStatus[];
    error?: string | null;
    /**
     * Whether not a single app left a trace in the storage account the dashboard reads, so
     * the verdicts are "no signal" across the board rather than a finding about any app.
     */
    hostStateElsewhere?: boolean;
    paymentSync?: PaymentSync | null;
    /** The build each app runs, as its instances report it; empty for a connector from before the report. */
    builds?: WorkerBuild[];
}

/** The build a function app runs, as its instances report it. */
export interface WorkerBuild {
    app: string;
    branch?: string | null;
    commit?: string | null;
    buildNumber?: string | null;
    reportedAt?: string | null;
    /** How many instances reported within the last day. */
    instances: number;
    /** The instances run different builds, as during a deploy. */
    mixed: boolean;
    /** The newest report is older than a day: it may not be what runs now. */
    stale: boolean;
}

export interface EnvironmentHealth {
    customer: string;
    environment: string;
    sampledAt: string;
    severity: HealthSeverity;
    summary: string;
    findings: Finding[];
    queues: QueueSnapshot | null;
    workers: WorkerFleet | null;
    exact: ExactConnection | null;
}

/** The state of one environment in a single call. */
export const getEnvironmentHealth = async (
    axios: AxiosInstance,
    customer: string,
    environment: string
): Promise<EnvironmentHealth> => {
    const response = await axios.get(
        `/Health/${encodeURIComponent(customer)}/${encodeURIComponent(
            environment
        )}`
    );

    // The axios instance is created with transformResponse: [], so response.data is a
    // string and has to be parsed here.
    return typeof response.data === 'string'
        ? (JSON.parse(response.data) as EnvironmentHealth)
        : (response.data as EnvironmentHealth);
};

/** The severities in order, so a caller can compare two of them. */
const SEVERITY_ORDER: HealthSeverity[] = [
    'Healthy',
    'Info',
    'Warning',
    'Critical',
];

/** Whether the first severity is worse than the second. */
export const isWorse = (a: HealthSeverity, b: HealthSeverity): boolean =>
    SEVERITY_ORDER.indexOf(a) > SEVERITY_ORDER.indexOf(b);

/**
 * What a worker verdict means on screen: the word, and whether it is good news.
 *
 * "Running" and "Alive" are deliberately not merged. A held lease proves an app is up right
 * now; a timer that fired an hour ago proves it was up an hour ago. When a queue in front
 * of a demonstrably running app goes untouched, the problem is the binding rather than the
 * app, and that distinction sends you to a different place entirely.
 */
export const WORKER_LABEL: Record<
    WorkerVerdict,
    { label: string; tone: 'good' | 'bad' | 'neutral' }
> = {
    Running: { label: 'draait nu', tone: 'good' },
    Alive: { label: 'leeft', tone: 'good' },
    Silent: { label: 'staat stil', tone: 'bad' },
    NoEvidence: { label: 'geen signaal', tone: 'neutral' },
    Unknown: { label: 'onbekend', tone: 'neutral' },
};
